import WebSocketChannel from "./webSocketChannel";
import { IFeatureFlagsService } from "./featureFlags";
import { FeatureFlagName } from "../utils/featureFlagName";
import { getServerDate } from "../utils/storage/date";
import * as logger from "../utils/logger";
import constants from "../constants";
import Service from "./base";

/**
 * Manages the drive-ping/drive-pong WebSocket liveness check for a single user session.
 *
 * Registered as the `@wsPing` scheduler task (1 min interval).
 * Each call to `tick()` represents one ping cycle.
 *
 * ## Failure detection
 *
 * Two failure modes are handled:
 *
 * 1. **Immediate throw** — `channel.send()` fails synchronously because the JS-side
 *    WebSocket is already closed.  A reconnect is scheduled with random jitter.
 *
 * 2. **PONG timeout** — `sendPing()` succeeds but no `drive-pong` arrives within
 *    `PING_TIMEOUT_MS` (10 s).  This catches the half-open TCP socket scenario confirmed
 *    in ESUPSD-49484 where the OS reports the socket as OPEN but server-to-client
 *    delivery is broken.
 *
 * ## Mobile resilience
 *
 * A single missed pong is treated as a suspected cellular gap and does NOT immediately
 * reconnect.  Only **two consecutive** missed pongs trigger a reconnect, giving the
 * connection time to recover from brief tower handoffs or LTE/5G transitions.
 *
 * The consecutive-miss counter resets whenever `lastPongReceivedAt` shows a pong
 * was received since the previous ping.
 *
 * ## JS-engine suspension (app backgrounded)
 *
 * When the JS engine is paused (e.g. app moves to background on mobile), `setTimeout`
 * callbacks fire immediately on resume regardless of how long the pause lasted.  A pong
 * timeout that fires several minutes after the ping was sent is indistinguishable from a
 * genuine miss without a staleness check.  If the elapsed time since the ping exceeds
 * `PING_INTERVAL_MS`, the timeout is discarded so a fresh probe can be sent by the next
 * scheduled tick.
 *
 * ## Reconnect jitter (thundering herd mitigation)
 *
 * All reconnects are delayed by a random value in `[0, RECONNECT_JITTER_MAX_MS)` before
 * calling the reconnect callback.  This spreads simultaneous reconnect attempts across
 * clients after a server restart so the server is not flooded when it comes back online.
 * Any pending jittered reconnect is cancelled automatically when the channel tears down
 * or the service is destroyed.
 *
 * All behaviour is gated behind `DRIVE.WEBSOCKET_PING_PONG.RELEASE`.
 */
export default class WebSocketPingService extends Service {
    /**
     * Number of consecutive ping cycles where a drive-pong was not received.
     * Resets to 0 when a pong arrives or on the throw path.
     * Reconnect is triggered when this reaches 2.
     */
    private consecutivePongTimeouts = 0;

    /**
     * Server-clock timestamp (ms) of the most recent `sendDrivePing()` call.
     * Compared against `lastPongReceivedAt` at the start of the next tick to determine
     * whether a pong was received in the intervening window.
     * Also used to detect stale pong-timeout callbacks caused by JS-engine suspension.
     */
    private lastPingSentAt = 0;

    /**
     * Server-clock timestamp (ms) of the most recent drive-pong received.
     * Updated by the pong handler registered with the channel in the constructor.
     * Zero until the first pong arrives.
     */
    private lastPongReceivedAt = 0;

    /** Handle for the pending pong-timeout timer; null when no ping is in flight. */
    private pingTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

    /**
     * Handle for the pending jittered-reconnect timer; null when no reconnect is
     * queued.  Cleared by `clearReconnect()` on teardown/destroy/offline transition.
     */
    private reconnectHandle: ReturnType<typeof setTimeout> | null = null;

    /** True while a jittered reconnect has been scheduled but not yet fired. */
    private reconnectPending = false;

    /**
     * True after channel pong/teardown handlers have been registered.
     *
     * The channel retains both handlers across teardown/reconnect, so this stays true for
     * the life of the service; only `destroy()` resets it, and it detaches the handlers at
     * the same time.
     */
    private handlersRegistered = false;

    /** True once destroy() has been called; prevents tick() from re-animating a destroyed service. */
    private isDestroyed = false;

    private readonly reconnect: () => void;

    /**
     * Stable handler references, captured once so `destroy()` can detach the same function
     * identity it registered. Inline closures would register a new function every call and
     * could never be unregistered.
     */
    private readonly onChannelTearDown: () => void;

    private readonly onPongReceived: () => void;

    constructor(
        private readonly webSocketChannel: WebSocketChannel,
        private readonly featureFlagsService: IFeatureFlagsService,
        private readonly user: string | null,
        reconnect?: () => void
    ) {
        super();
        this.reconnect = reconnect ?? (() => this.webSocketChannel.startSubscriptions());

        // Cancel any in-flight pong timer and pending jittered reconnect when the channel
        // tears down, so neither can fire against the replacement channel.
        this.onChannelTearDown = () => {
            this.clearPingTimeout();
            this.clearReconnect();
        };

        // Stamp pong arrival and cancel the pending timeout.
        this.onPongReceived = () => {
            this.lastPongReceivedAt = getServerDate().getTime();
            this.clearPingTimeout();
        };
    }

    /**
     * Executes one polling cycle.  Called by the `@wsPing` scheduler task.
     */
    tick(isOnline = true): void {
        if (this.isDestroyed) {
            return;
        }
        if (!this.featureFlagsService.hasFeatureFlag(FeatureFlagName.WEBSOCKET_PING_PONG) || !isOnline) {
            this.consecutivePongTimeouts = 0;
            this.clearPingTimeout();
            this.clearReconnect();
            return;
        }

        this.ensureHandlersRegistered();

        // If the previous ping got a pong since it was sent, that cycle was healthy —
        // reset the consecutive-miss counter.
        if (this.lastPingSentAt > 0 && this.lastPongReceivedAt > this.lastPingSentAt) {
            this.consecutivePongTimeouts = 0;
        }

        if (!this.webSocketChannel.isInitialized()) {
            this.clearPingTimeout();
            logger.log(logger.Source.CHANNEL, `WebSocket is closed. ${ this.webSocketChannel.getChannelDiagnostics() }`, this.user);
            this.scheduleReconnect();
            return;
        }

        this.lastPingSentAt = getServerDate().getTime();

        try {
            // Cancel any stale timer from a previous ping before starting a fresh one.
            this.clearPingTimeout();
            this.pingTimeoutHandle = setTimeout(() => {
                this.pingTimeoutHandle = null;

                // Guard against timeouts that fire long after their scheduled time due to the
                // JS engine being suspended (e.g. app backgrounded on mobile).  When the engine
                // resumes, all overdue callbacks fire immediately — a pong timeout from before
                // the pause is not evidence of a broken connection.  Discard it and let the
                // next scheduled tick send a fresh probe.
                const elapsed = getServerDate().getTime() - this.lastPingSentAt;
                if (elapsed > constants.WEBSOCKETS.PING_INTERVAL_MS) {
                    logger.log(
                        logger.Source.CHANNEL,
                        `WebSocket PONG timeout discarded — fired ${ Math.round(elapsed / 1000) }s after ping (JS engine was paused).`,
                        this.user
                    );
                    return;
                }

                this.consecutivePongTimeouts++;

                const diagnostics = this.webSocketChannel.getChannelDiagnostics();

                if (this.consecutivePongTimeouts >= 2) {
                    // Two consecutive ticks with no pong: confident the connection is
                    // broken, not just a momentary mobile network hiccup.
                    logger.error(logger.Source.CHANNEL, `WebSocket PONG timeout (${ this.consecutivePongTimeouts } consecutive) — reconnecting. ${ diagnostics }`, this.user);
                    this.consecutivePongTimeouts = 0;
                    this.scheduleReconnect();
                } else {
                    // First miss — could be a brief cellular gap. Log and wait for the
                    // next tick to confirm before tearing down the connection.
                    logger.log(logger.Source.CHANNEL, `WebSocket PONG timeout (1 of 2 needed to reconnect) — waiting for next tick. ${ diagnostics }`, this.user);
                }
            }, constants.WEBSOCKETS.PING_TIMEOUT_MS);
            this.webSocketChannel.sendDrivePing();
            logger.log(logger.Source.CHANNEL, "WebSocket drive-ping sent.", this.user);
        } catch (e) {
            // channel.send() threw: JS-side socket is definitively dead.
            // Clear the pong timeout that was queued before the throw so it cannot fire
            // during the jitter window and spuriously increment the consecutive-miss counter.
            this.clearPingTimeout();
            this.consecutivePongTimeouts = 0;
            const diagnostics = this.webSocketChannel.getChannelDiagnostics();
            logger.error(logger.Source.CHANNEL, `WebSocket ping failed: ${ e instanceof Error ? e.message : String(e) }. ${ diagnostics }`, this.user);
            this.scheduleReconnect();
        }
    }

    destroy(): void {
        this.isDestroyed = true;
        this.clearPingTimeout();
        this.clearReconnect();
        this.consecutivePongTimeouts = 0;
        this.lastPingSentAt = 0;
        this.lastPongReceivedAt = 0;
        // Detach from the channel. The channel outlives this service and retains handlers
        // across reconnects, so leaving them attached would keep invoking callbacks that
        // mutate a destroyed service and prevent GC.
        this.webSocketChannel.unregisterTearDownHandler(this.onChannelTearDown);
        this.webSocketChannel.unregisterPongHandler(this.onPongReceived);
        this.handlersRegistered = false;
    }

    private ensureHandlersRegistered(): void {
        if (this.handlersRegistered) {
            return;
        }

        // Both handlers are retained by the channel across teardown/reconnect, so this
        // registration happens once per service and is undone only by destroy().
        this.webSocketChannel.registerPongHandler(this.onPongReceived);
        this.webSocketChannel.registerTearDownHandler(this.onChannelTearDown);

        this.handlersRegistered = true;
    }

    /**
     * Schedules a reconnect after a random jitter delay in `[0, RECONNECT_JITTER_MAX_MS)`.
     * No-ops when a reconnect is already pending, so duplicate calls are safe.
     */
    private scheduleReconnect(): void {
        if (this.reconnectPending) {
            return;
        }
        this.reconnectPending = true;
        const jitterMs = Math.floor(Math.random() * constants.WEBSOCKETS.RECONNECT_JITTER_MAX_MS);
        this.reconnectHandle = setTimeout(() => {
            this.reconnectHandle = null;
            this.reconnectPending = false;
            this.reconnect();
        }, jitterMs);
    }

    private clearPingTimeout(): void {
        if (this.pingTimeoutHandle !== null) {
            clearTimeout(this.pingTimeoutHandle);
            this.pingTimeoutHandle = null;
        }
    }

    private clearReconnect(): void {
        if (this.reconnectHandle !== null) {
            clearTimeout(this.reconnectHandle);
            this.reconnectHandle = null;
            this.reconnectPending = false;
        }
    }
}
